<template>
  <div id="WatchClock" :style="{maxHeight: `${defaultHeight}px`}"></div>
</template>

<script setup>
import moment from "moment"

import p5 from 'p5'
import {ref, reactive, watchEffect, onUnmounted, onMounted, watch} from 'vue'
import defaultValue from "@/utils/defaultValue"
import colorTool from "@/utils/colorTool";

const {changeToRgb} = colorTool()

const {defaultWidth, defaultHeight} = defaultValue()
const props = defineProps(['effectTheme'])

const effectThemeData = reactive(props.effectTheme)
let canvasWrap = ref('stop')
let backgroundColor = ref('rgb(0,0,0)')
let drawPot = ref(0)

let nowDateTimeObj = reactive({obj: {}})
let timeTiger = ref(null)
function startTime() {
  if (timeTiger.value) clearTimeout(timeTiger.value)
  const nowDate = moment().format('MMM,DD,ddd,HH:mm,HH,mm,ss,SSS').split(',')
  const timeTextObj = {
    m: nowDate[0].toUpperCase(), // 月
    d: nowDate[1], // 日
    weekday: nowDate[2].toUpperCase(), // 星期
    time: nowDate[3], // 時間
    hour: nowDate[4],
    min: nowDate[5],
    sec: `${nowDate[6]}.${nowDate[7]}`
  }
  nowDateTimeObj.obj = timeTextObj
  timeTiger.value = setTimeout(function () {
    startTime()
  }, 150)
}


const watchClockAnimate = (sketch) => {
  let cw = defaultWidth.value
  let ch = defaultHeight.value
  let hourHand
  let minuteHand
  let secondHand
  let mainClock
  let borderBg2
  let fontFamily

  sketch.preload = function () {
    hourHand = sketch.loadImage(require('@/assets/watchClock/hourHand.png'))
    minuteHand = sketch.loadImage(require('@/assets/watchClock/minuteHand.png'))
    secondHand = sketch.loadImage(require('@/assets/watchClock/secondHand.png'))
    mainClock = sketch.loadImage(require('@/assets/watchClock/mainClock.png'))
    borderBg2 = sketch.loadImage(require('@/assets/watchClock/borderBg2.png'))
    fontFamily = sketch.loadFont(require('@/assets/font/manolo-mono/Manolo_Mono.ttf'))
  }

   sketch.setup = function () {
    sketch.createCanvas(cw, ch);
    sketch.frameRate(40); // 刷新頻率
    sketch.angleMode(sketch.DEGREES);
    sketch.textFont(fontFamily)
     sketch.noStroke()
    sketch.smooth()
  }

  sketch.draw = function () {
    drawPot.value++
    sketch.clear()
    sketch.background(0)
    let hour = nowDateTimeObj.obj.hour
    let min = nowDateTimeObj.obj.min
    let sec = nowDateTimeObj.obj.sec

    handleBackground(hour, min, sec, backgroundColor.value)
    handleFont()
    handleClock (hour, min, sec)
  }

  function handleFont() {
    sketch.push();
    sketch.rectMode(sketch.CENTER)
    sketch.textAlign(sketch.CENTER, sketch.CENTER)
    sketch.fill(255,255,255)
    // 電子時間
    sketch.textSize(74)
    sketch.text(nowDateTimeObj.obj.time, sketch.width/2,64)
    // 月
    sketch.textSize(40)
    sketch.text(nowDateTimeObj.obj.m, 86, sketch.height - 152, 40, 40)
    // 日
    sketch.textSize(45)
    sketch.text(nowDateTimeObj.obj.d, sketch.width - 88, sketch.height - 152, 40, 40)
    //禮拜
    sketch.textSize(48)
    sketch.text(nowDateTimeObj.obj.weekday, sketch.width/2 - 3, sketch.height - 84, 50, 50)
    sketch.pop()
  }

  function handleBackground (hour, min, sec, bgColor) {
    // 背景動態
    sketch.push();
    let centerX = sketch.width / 2; // 正方形中心的x座標
    let centerY = sketch.height / 2; // 正方形中心的y座標
    let radius = sketch.width; // 半徑
    let startAngle = -90 - 1.1; // 起始角度
    let endAngle = (sec * 6) - 90 + 1.1  ; // 結束角度
    sketch.fill(bgColor);
    sketch.arc(centerX, centerY,  radius * 2, radius * 2, startAngle, endAngle);
    sketch.pop()

    // 背景遮蓋 main, mainClock 變色
    sketch.push()
    sketch.fill(bgColor)
    sketch.rect(20, 20, sketch.width  - 40, sketch.height - 40);
    sketch.pop()

    // 背景圖 mainClock
    sketch.background(mainClock)
    //  背景圖 borderBg point
    sketch.push()
    let pointW = 8
    let pointH = 8
    sketch.fill(bgColor)
    sketch.rect((sketch.width/4*1) - pointW * 2 - 1, 0, pointW, pointH); // 11:00
    sketch.rect(sketch.width/2 - 3.6, 0, pointW, pointH); // 0:00
    sketch.rect((sketch.width/4*3) + pointW, 0, pointW, pointH); // 1:00
    sketch.rect(sketch.width - pointW, (sketch.height/4*1) - pointH - 9, pointW, pointH); // 2:00
    sketch.rect(sketch.width - pointW, sketch.height/2 - 4.8, pointW, pointH); // 3:00
    sketch.rect(sketch.width - pointW, (sketch.height/4*3) + pointH, pointW, pointH); // 4:00
    sketch.rect((sketch.width/4*3) + pointW, sketch.height - pointH, pointW, pointH); // 5:00
    sketch.rect(sketch.width/2 - 3.6, sketch.height - pointH, pointW, pointH); // 6:00
    sketch.rect((sketch.width/4*1) - pointW * 2 - 0.6, sketch.height - pointH, pointW, pointH); // 7:00
    sketch.rect(0, (sketch.height/4*3) + pointH, pointW, pointH); // 8:00
    sketch.rect(0, sketch.height/2 - 4.8, pointW, pointH); // 9:00
    sketch.rect(0, (sketch.height/4*1) - pointH - 9, pointW, pointH); // 10:00
    sketch.pop()

    // 背景圖 borderBg
    sketch.background(borderBg2)
  }

  function handleClock (hour, min, sec) {
    // 分針
    sketch.push();
    sketch.imageMode(sketch.CENTER);
    sketch.translate(sketch.width / 2, sketch.height / 2);
    sketch.rotate(min / 60 * 360);
    sketch.image(minuteHand, 0, -70)
    sketch.pop()

    // 時針
    sketch.push();
    sketch.imageMode(sketch.CENTER);
    sketch.translate(sketch.width / 2, sketch.height / 2);
    sketch.rotate(hour / 12 * 360);
    sketch.image(hourHand, 0, -65)
    sketch.pop()


    // 秒針
    sketch.push();
    sketch.imageMode(sketch.CENTER);
    sketch.translate(sketch.width /2, sketch.height/2);
    sketch.rotate(sketch.round(sec / 60, 4)  * 360);
    sketch.imageMode(sketch.CENTER)
    sketch.image(secondHand, 0, -80)
    sketch.pop()
  }
}

function initAnimate () {
  if (canvasWrap.value !== 'stop') {
    canvasWrap.value.remove()
    canvasWrap.value = 'stop'
  }
}

watch(drawPot, (n, o) => {
  if (n > 72000) { // 30 min init (40)
    initAnimate()
    canvasWrap.value = new p5(watchClockAnimate, 'WatchClock')
    drawPot.value = 0
  }
})

watchEffect(() => {
  if (timeTiger.value) clearTimeout(timeTiger.value)
  startTime()
  const {
    BackgroundColor
  } = effectThemeData

  // 背景色
  backgroundColor.value = typeof BackgroundColor === "object" && BackgroundColor.length > 0 ? changeToRgb(BackgroundColor) : 'rgb(0,0,0)'
})

onMounted(() => {
  if (canvasWrap.value === 'stop') canvasWrap.value = new p5(watchClockAnimate, 'WatchClock')
})

onUnmounted(() => {
  drawPot.value = 0
  backgroundColor.value = 'rgb(0,0,0)'
  initAnimate()
  if (timeTiger.value) clearTimeout(timeTiger.value)
})

</script>

